import { useCallback, useState } from 'react';

import {
  fetchForwardGeocode,
  fetchReverseGeocode,
  type MapboxResult,
} from '../helpers/mapbox';

interface UseForwardGeocodeResult {
  data: MapboxResult | null;
  loading: boolean;
  error: string | null;
  geocode: (query: string) => Promise<MapboxResult | undefined>;
}

export function useForwardGeocode(): UseForwardGeocodeResult {
  const [data, setData] = useState<MapboxResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const geocode = useCallback(async (query: string) => {
    if (!query.trim()) {
      setError('Query cannot be empty');
      return;
    }

    setLoading(true);
    setError(null);
    // if query is a coordinate pair, use reverse geocoding
    if (/^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(query)) {
      const coords = query.split(',').map((coord) => coord.trim());
      if (
        coords.length !== 2 ||
        isNaN(Number(coords[0])) ||
        isNaN(Number(coords[1]))
      ) {
        setError('Invalid coordinates format');
        setLoading(false);
        return;
      }
      const response = await fetchReverseGeocode(
        Number(coords[1]),
        Number(coords[0]),
      );
      const result = await response.json();
      setData(result);
      return result;
    }

    try {
      const response = await fetchForwardGeocode(query);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setData(result);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, geocode };
}
